import { assignPseudonyms, type Pseudonym } from '../sanitise/pseudonyms';
import type { VaultRecordPlain } from './vaultSession';

/**
 * Splitting an imported roster: pseudonyms to the cloud, real details to the vault.
 *
 * ── THE SHAPE OF THE PROBLEM ────────────────────────────────────────────────────────────────────
 *
 * `StudentRosterImportModal` already parses in the browser and makes no network call, so the real names
 * are in memory and have been nowhere else. This is the one moment they can be split cleanly: before
 * anything is written, rather than written and then migrated — which is the whole reason the migration
 * exists and why it was the most dangerous code in this feature.
 *
 * ── WHY ORDER-PRESERVING PSEUDONYMS, NOT THE CURATED FIFTY ──────────────────────────────────────
 *
 * `maskNames.ts` has fifty hand-picked animals; `sanitise/pseudonyms.ts` assigns words that preserve the
 * roster's ALPHABETICAL ORDER. The second is right here, for a reason that only matters when something has
 * gone wrong: a teacher who has lost both their passphrase and their recovery code can still work out who
 * is who, because position fourteen in the pseudonymous roster is position fourteen on the paper list they
 * imported from. It is a real recovery path, and it costs nothing.
 *
 * It is also an honest re-identification trade, already stated to teachers by the sanitise modal: anyone
 * holding the original class list can map the pseudonyms back. That is true of any order-preserving
 * scheme, and the alternative — random order — makes the data no safer against someone with the list
 * while removing the teacher's only way back.
 *
 * ── WHAT STAYS READABLE ─────────────────────────────────────────────────────────────────────────
 *
 * The pseudonym, and nothing else about who the child is. `studentNumber` goes to the vault too, because
 * an Ontario student number is a direct identifier on its own — and nothing server-side needs it in a
 * Privacy First class: the duplicate check skips nickname classes, nickname joins stamp their own, and
 * SIS and OnSIS both require a schoolId, which these classes cannot have.
 */

/** Fields that move to the vault. Everything identifying, per Reece's decision. */
export const VAULTED_IMPORT_FIELDS = [
  'firstName', 'lastName', 'preferredName', 'email', 'studentNumber',
  'governmentId', 'parentName', 'parentEmail', 'parentPhone', 'pronouns',
] as const;

/** A parsed import row, before it is split. */
export interface ImportRow {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  studentNumber?: string;
  governmentId?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  pronouns?: string;
  /** Anything else the importer produced — course codes, notes — rides along to the class document. */
  [key: string]: unknown;
}

export interface SplitRow {
  /** What gets written to the class document: the pseudonym plus any non-identifying extras. */
  publicPart: Record<string, unknown>;
  /** What gets encrypted. Absent fields are simply not in the record. */
  vaultPart: VaultRecordPlain;
}

export interface ImportPlan {
  rows: SplitRow[];
  /**
   * Rows no pseudonym could be found for, by index. REPORTED, never silently imported under a real name —
   * which would put the thing the mode exists to prevent onto the class document.
   */
  exhausted: number[];
}

const nonEmpty = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
};

/**
 * Split an imported roster.
 *
 * Pure, so the teacher can be shown exactly what will be stored before anything is written — and so this
 * decision is testable without a database.
 *
 * `alreadyIssued` carries the pseudonyms the class has already handed out, so importing a second batch in
 * September does not reissue a word and leave two children answering to Otter.
 */
export function planVaultImport(
  rows: readonly ImportRow[],
  alreadyIssued: readonly Pseudonym[] = [],
): ImportPlan {
  /**
   * `assignPseudonyms` keys by student id, which an imported row does not have yet, so the index stands in.
   * It is stable for the length of this function, which is all that is needed.
   */
  const nameable = rows.map((r, i) => ({
    id: String(i),
    firstName: nonEmpty(r.firstName),
    lastName: nonEmpty(r.lastName),
  }));

  const { assignments, exhausted } = assignPseudonyms(nameable, {
    alreadyUsed: {
      firstNames: alreadyIssued.map(p => p.firstName),
      lastNames: alreadyIssued.map(p => p.lastName),
    },
  });

  const exhaustedIdx = exhausted.map(id => Number(id)).filter(n => Number.isFinite(n));
  const plan: ImportPlan = { rows: [], exhausted: exhaustedIdx };

  rows.forEach((row, i) => {
    if (exhaustedIdx.includes(i)) return;
    const pseudo = assignments.get(String(i));
    if (!pseudo) { plan.exhausted.push(i); return; }

    const vaultPart: VaultRecordPlain = {};
    const publicPart: Record<string, unknown> = {};

    for (const [k, v] of Object.entries(row)) {
      if ((VAULTED_IMPORT_FIELDS as readonly string[]).includes(k)) {
        const s = nonEmpty(v);
        if (s) (vaultPart as Record<string, unknown>)[k] = s;
        continue;
      }
      // Everything not identifying rides along to the class document unchanged.
      publicPart[k] = v;
    }

    /**
     * The pseudonym OVERWRITES firstName/lastName on the public part, and `studentNumber` is blanked
     * rather than omitted.
     *
     * Blanked because an absent field reads as "never had one", and a teacher looking at a vaulted roster
     * should be able to see that the slot is empty because it was emptied — the same reasoning as the
     * migration. Omitting it would also let a later writer treat the student as never having had a number
     * and happily write one back.
     */
    publicPart.firstName = pseudo.firstName;
    publicPart.lastName = pseudo.lastName;
    publicPart.studentNumber = '';
    /**
     * STAMPED AS A PLACEHOLDER, so later code can tell this name from a real one.
     *
     * There was no way to tell. `convertLiveClass` has to answer "is the name on this row already
     * safe to leave on a classmate-readable array?", and without a marker it read every placeholder
     * this function issues as a real name: it counted every protected class as unconverted, renamed
     * the whole roster on each run, and — for a row whose seal had failed, so there was no vault
     * record either — sealed the PLACEHOLDER as the child's real name, which the vault then held as
     * the only copy.
     *
     * A boolean on the row rather than a guess from the words, because a guess is wrong in one
     * direction or the other and both are bad: treat a real name as safe and it stays readable under
     * a banner claiming protection; treat a placeholder as real and the vault is corrupted.
     */
    publicPart.placeholderName = true;

    plan.rows.push({ publicPart, vaultPart });
  });

  return plan;
}

/** What the teacher is told before the import runs. Counts and field names, never a value. */
export function describeImportPlan(plan: ImportPlan): string {
  const n = plan.rows.length;
  if (!n && !plan.exhausted.length) return 'Nothing to import.';

  const vaulted = new Set<string>();
  for (const r of plan.rows) for (const k of Object.keys(r.vaultPart)) vaulted.add(k);

  const parts = [
    `${n} student${n === 1 ? '' : 's'} will be added under a placeholder name.`,
  ];
  if (vaulted.size) {
    parts.push(
      `Their ${[...vaulted].length === 1 ? 'detail' : 'details'} (${[...vaulted].join(', ')}) will be `
      + 'encrypted on this device — we store only the encrypted version and cannot read it.',
    );
  }
  if (plan.exhausted.length) {
    parts.push(
      `${plan.exhausted.length} row${plan.exhausted.length === 1 ? '' : 's'} could not be given a `
      + 'placeholder and will NOT be imported. Importing them under their real name would defeat the point '
      + 'of this mode, so they are left out for you to handle.',
    );
  }
  return parts.join(' ');
}

/**
 * Split an EDIT to one student, the way `planVaultImport` splits a new one.
 *
 * ── WHY THIS HAS TO EXIST ───────────────────────────────────────────────────────────────────────
 *
 * Adding and importing were both routed; editing was not. A teacher correcting a misspelled surname, or
 * typing in a guardian's email, went straight through `updateStudent` to the class document and the
 * owner-only roster — in plaintext, in the one mode that promises otherwise.
 *
 * It was not silent, which is the only mercy: the leak guard compares VALUES, and a name the vault has
 * already decrypted is a value it knows, so `assertNoVaultLeak` threw. But what the teacher got was a
 * developer-facing error telling them to read names through `resolveDisplayName` — on the ordinary act of
 * fixing a spelling. And the guard only catches a value it has SEEN decrypted: typing a corrected
 * spelling, a new email address, or a guardian's phone number for the first time is a value it has never
 * seen, so those went through.
 *
 * Reuses `VAULTED_IMPORT_FIELDS` deliberately. Which fields are identifying is one decision; three copies
 * of it is how the add path, the import path and the edit path come to disagree about `pronouns`.
 */
export interface SplitUpdate {
  /** Fields that may be written to the class document and the roster. */
  publicPart: Record<string, unknown>;
  /** Fields to merge into the encrypted record. */
  vaultPart: VaultRecordPlain;
}

/**
 * Note the difference from `planVaultImport`: this does NOT blank anything or assign a pseudonym.
 *
 * An edit touches only the keys it carries. Blanking `studentNumber` here would wipe it on every
 * unrelated edit, and re-assigning a pseudonym would rename the child in front of their classmates
 * because a teacher fixed a phone number.
 */
export function splitStudentUpdate(updates: Record<string, unknown>): SplitUpdate {
  const publicPart: Record<string, unknown> = {};
  const vaultPart: VaultRecordPlain = {};

  for (const [k, v] of Object.entries(updates)) {
    /**
     * `undefined` MEANS "NOT EDITED" AND IS DROPPED. An empty string means "cleared" and is kept.
     *
     * This distinction is the difference between a working edit form and one that destroys records.
     * `Object.entries` includes keys whose value is undefined, and `updateStudentPii` merges what it is
     * given — so an edit form that prefilled a field as `undefined` (because the value lives in the
     * vault and the form read the class document) sent `{email: undefined, parentName: undefined, ...}`,
     * the merge wrote those over the real values, and `JSON.stringify` in `sealRecord` then dropped the
     * keys entirely. Changing one field deleted every other one.
     *
     * Clearing a field deliberately still works, because the form sends `''` for that and `''` survives.
     */
    if (v === undefined) continue;

    if ((VAULTED_IMPORT_FIELDS as readonly string[]).includes(k)) {
      (vaultPart as Record<string, unknown>)[k] = typeof v === 'string' ? v.trim() : v;
      continue;
    }
    publicPart[k] = v;
  }

  return { publicPart, vaultPart };
}
